import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import usePomodoro from '../utils/usePomodoro';
import useTimerSound from '../utils/useTimerSound';
import useTimerWindow from '../utils/useTimerWindow';

const FocusToolsContext = createContext(null);

export function FocusToolsProvider({ children }) {
  const tools = usePomodoro();
  const location = useLocation();
  const [focusOpen, setFocusOpen] = useState(() => /^\/tools\/pomodoro\/?$/.test(location.pathname)
    && ['running', 'paused'].includes(tools.state.timer.status));
  const sound = useTimerSound(tools.state.timer, tools.state.soundEnabled);
  const popout = useTimerWindow();
  const previousPath = useRef(location.pathname);
  useEffect(() => {
    if (previousPath.current !== location.pathname) setFocusOpen(false);
    previousPath.current = location.pathname;
  }, [location.pathname]);

  const act = (type, values) => {
    if (type === 'start' || type === 'resume') {
      if (tools.state.soundEnabled) sound.prepare();
      if (!popout.timerWindow) setFocusOpen(true);
    }
    if (type === 'sound' && values.enabled) sound.prepare();
    if (['reset', 'acknowledge'].includes(type)) {
      setFocusOpen(false);
      sound.stop();
    }
    tools.act(type, values);
  };

  const openTimerWindow = async () => {
    const target = await popout.openTimerWindow();
    if (target) setFocusOpen(false);
  };

  return (
    <FocusToolsContext.Provider value={{ ...tools, ...popout, openTimerWindow, act, focusOpen, setFocusOpen, soundBlocked: sound.soundBlocked }}>
      {children}
    </FocusToolsContext.Provider>
  );
}

export function useFocusTools() {
  const context = useContext(FocusToolsContext);
  if (!context) throw new Error('Focus tools require FocusToolsProvider');
  return context;
}
